// SpotlightColorEditor.jsx
import React, { useState, useEffect } from "react";
import { Box, Button, Typography, SwipeableDrawer } from "@mui/material";

// Reusable ColorPicker Component
const ColorPicker = ({ label, value, onChange }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: "14px", mb: 0.5 }}>{label}:</Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        p: 1,
      }}
    >
      <input
        type="text"
        value={value}
        placeholder="Select Color"
        onChange={() => null}
        style={{
          border: "none",
          outline: "none",
          flex: 1,
          fontSize: "14px",
        }}
      />
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "24px",
          height: "24px",
          padding: 0,
          border: "none",
        }}
      />
    </Box>
  </Box>
);

// Main Component
const SpotlightColorEditor = ({ onSave, data, edit, setEdit }) => {
  const initialColors = data?.colors || null;
  const [colors, setColors] = useState({
    defaultTitle: "",
    defaultBody: "",
    defaultButton: "",
    defaultBorder: "",
    hoverTitle: "",
    hoverButton: "",
    hoverBody: "",
    hoverBorder: "",
    activeTitle: "",
    activeBody: "",
    activeButton: "",
    activeBorder: "",
  });

  useEffect(() => {
    if (initialColors) {
      setColors({ ...initialColors });
    } else {
      setColors({
        defaultTitle: "#000000",
        defaultBody: "#0000000",
        defaultButton: "#FFFFFF",
        defaultBorder: "#E0E0E0",
        hoverTitle: "#000000",
        hoverBody: "#FFFFFF",
        hoverButton: "#FFFFFF",
        hoverBorder: "#E0E0E0",
        activeTitle: "#000000",
        activeBody: "#FFFFFF",
        activeButton: "#FFFFFF",
        activeBorder: "#E0E0E0",
      });
    }
  }, [initialColors]);

  const handleSave = () => {
    onSave(colors);
  };

  return (
    <SwipeableDrawer
      anchor={"right"}
      open={edit.open}
      onClose={() =>
        setEdit({
          open: false,
          edit: "",
          title: "",
        })
      }
      onOpen={() => {}}
      PaperProps={{
        sx: {
          minHeight: "100vh",
          width: "300px",
          paddingBottom: "100px",
          overflowX: "hidden",
          overflowY: "scroll",
          /* Custom scrollbar styling */
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f4f4f4",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          p: 2,
          width: "300px",
        }}
      >
        <Typography
          sx={{
            borderBottom: "1px solid #e0e0e0",
            pb: 1,
            mb: 2,
            fontSize: "14px",
          }}
        >
          Spotlight | Edit Colors
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontSize: "14px",
              mb: 1,
              color: colors.defaultTitle,
              transition: "all 0.2s",
              cursor: "pointer",
              "&:hover": {
                color: colors.hoverTitle,
              },
              "&:active": {
                color: colors.activeTitle,
              },
            }}
          >
            {data.title}
          </Typography>
          {data.body && (
            <Typography
              sx={{
                fontSize: "12px",
                mb: 1,
                color: colors.defaultBody,
                transition: "all 0.2s",
                cursor: "pointer",
                "&:hover": {
                  color: colors.hoverBody,
                },
                "&:active": {
                  color: colors.activeBody,
                },
              }}
            >
              {data.body}
            </Typography>
          )}

          <Box
            sx={{
              height: "40px",
              border: `1px solid ${colors.defaultBorder || "#e0e0e0"}`,
              borderRadius: "4px",
              backgroundColor: colors.defaultButton || "#ffffff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              cursor: "pointer",
              "&:hover": {
                backgroundColor: colors.hoverButton || "#f5f5f5",
                border: `1px solid ${colors.hoverBorder || "#e0e0e0"}`,
              },
              "&:active": {
                backgroundColor: colors.activeButton || "#eeeeee",
                border: `1px solid ${colors.activeBorder || "#e0e0e0"}`,
              },
            }}
          >
            Example Button
          </Box>
        </Box>

        <ColorPicker
          label="Default Title Color"
          value={colors.defaultTitle}
          onChange={(value) => setColors({ ...colors, defaultTitle: value })}
        />
        {data.body && (
          <ColorPicker
            label="Default Body Color"
            value={colors.defaultBody}
            onChange={(value) => setColors({ ...colors, defaultBody: value })}
          />
        )}
        <ColorPicker
          label="Default Button Color"
          value={colors.defaultButton}
          onChange={(value) => setColors({ ...colors, defaultButton: value })}
        />
        <ColorPicker
          label="Default Border Color"
          value={colors.defaultBorder}
          onChange={(value) => setColors({ ...colors, defaultBorder: value })}
        />
        <ColorPicker
          label="Hover Title Color"
          value={colors.hoverTitle}
          onChange={(value) => setColors({ ...colors, hoverTitle: value })}
        />
        {data.body && (
          <ColorPicker
            label="Hover Body Color"
            value={colors.hoverBody}
            onChange={(value) => setColors({ ...colors, hoverBody: value })}
          />
        )}
        <ColorPicker
          label="Hover Button Color"
          value={colors.hoverButton}
          onChange={(value) => setColors({ ...colors, hoverButton: value })}
        />
        <ColorPicker
          label="Hover Border Color"
          value={colors.hoverBorder}
          onChange={(value) => setColors({ ...colors, hoverBorder: value })}
        />
        <ColorPicker
          label="Click/Active Title Color"
          value={colors.activeTitle}
          onChange={(value) => setColors({ ...colors, activeTitle: value })}
        />
        {data.body && (
          <ColorPicker
            label="Click/Active Body Color"
            value={colors.activeBody}
            onChange={(value) => setColors({ ...colors, activeBody: value })}
          />
        )}
        <ColorPicker
          label="Click/Active Button Color"
          value={colors.activeButton}
          onChange={(value) => setColors({ ...colors, activeButton: value })}
        />
        <ColorPicker
          label="Click/Active Border Color"
          value={colors.activeBorder}
          onChange={(value) => setColors({ ...colors, activeBorder: value })}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: "#2e7d32",
            color: "white",
            "&:hover": {
              bgcolor: "#1b5e20",
            },
          }}
        >
          Save
        </Button>
      </Box>
    </SwipeableDrawer>
  );
};

export default SpotlightColorEditor;
